import { Button } from '@hive/component';
import { SettingRow } from '@hive/component/setting-components';
import { SubscriptionService } from '@hive/core/modules/cloud';
import {
  SubscriptionPlan,
  SubscriptionRecurring,
  SubscriptionStatus,
} from '@hive/graphql';
import { type I18nString, i18nTime, Trans, useI18n } from '@hive/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useEffect, useState } from 'react';

import { CancelAction } from '../plans/actions';
import { CardNameLabelRow } from './card-name-label-row';
import { PaymentMethodUpdater } from './payment-method';
import * as styles from './style.css';

const DescriptionI18NKey = {
  Basic: 'com.hive.payment.billing-setting.current-plan.description',
  Monthly: 'com.hive.payment.billing-setting.current-plan.description.monthly',
  Yearly: 'com.hive.payment.billing-setting.current-plan.description.yearly',
  Lifetime:
    'com.hive.payment.billing-setting.current-plan.description.lifetime',
} as const satisfies { [key: string]: I18nString };

const getMessageKey = (
  plan: SubscriptionPlan | null,
  recurring: SubscriptionRecurring
) => {
  if (plan !== SubscriptionPlan.Team) {
    return DescriptionI18NKey.Basic;
  }
  return DescriptionI18NKey[recurring];
};

export const ProPlanCard = ({
  gotoCloudPlansSetting,
}: {
  gotoCloudPlansSetting: () => void;
}) => {
  const t = useI18n();
  const subscriptionService = useService(SubscriptionService);
  useEffect(() => {
    subscriptionService.subscription.revalidate();
    subscriptionService.prices.revalidate();
  }, [subscriptionService]);
  const teamSubscription = useLiveData(subscriptionService.subscription.pro$);

  const teamPrice = useLiveData(subscriptionService.prices.teamPrice$);

  const currentPlan = teamSubscription?.plan ?? null;
  const currentRecurring =
    teamSubscription?.recurring ?? SubscriptionRecurring.Monthly;

  const amount = teamSubscription
    ? teamPrice
      ? teamSubscription.recurring === SubscriptionRecurring.Monthly
        ? String((teamPrice.amount ?? 0) / 100)
        : String((teamPrice.yearlyAmount ?? 0) / 100)
      : '?'
    : '0';

  return (
    <div className={styles.planCard}>
      <div className={styles.currentPlan}>
        <SettingRow
          spreadCol={false}
          name={
            <CardNameLabelRow
              cardName={t['com.hive.payment.billing-setting.current-plan']()}
              status={teamSubscription?.status}
            />
          }
          desc={
            <>
              <Trans
                i18nKey={getMessageKey(currentPlan, currentRecurring)}
                values={{
                  planName: currentPlan,
                }}
                components={{
                  1: (
                    <span
                      onClick={gotoCloudPlansSetting}
                      className={styles.currentPlanName}
                    />
                  ),
                }}
              />
              <CloudExpirationInfo />
            </>
          }
        />
        <PlanAction
          plan={currentPlan}
          subscriptionStatus={teamSubscription?.status}
          gotoPlansSetting={gotoCloudPlansSetting}
          isCanceled={!!teamSubscription?.canceledAt}
        />
      </div>
      <p className={styles.planPrice}>
        ${amount}
        <span className={styles.billingFrequency}>
          /
          {currentRecurring === SubscriptionRecurring.Monthly
            ? t['com.hive.payment.billing-setting.month']()
            : t['com.hive.payment.billing-setting.year']()}
        </span>
      </p>
    </div>
  );
};

const CloudExpirationInfo = () => {
  const t = useI18n();
  const subscriptionService = useService(SubscriptionService);
  const subscription = useLiveData(subscriptionService.subscription.pro$);

  let text = '';

  if (
    subscription?.status === SubscriptionStatus.Trialing &&
    subscription?.trialEnd
  ) {
    // Show trial end date for users on trial
    text =
      t['com.hive.payment.billing-setting.trial-end.description']?.({
        trialEnd: i18nTime(subscription.trialEnd, {
          absolute: { accuracy: 'day' },
        }),
      }) ??
      `Your trial ends on ${i18nTime(subscription.trialEnd, { absolute: { accuracy: 'day' } })}`;
  } else if (subscription?.status === SubscriptionStatus.PastDue) {
    text = t['com.hive.payment.billing-tip.past-due']({
      due: i18nTime(subscription.nextBillAt, {
        absolute: { accuracy: 'day' },
      }),
    });
  } else if (subscription?.nextBillAt) {
    text = t['com.hive.payment.billing-setting.renew-date.description']({
      renewDate: i18nTime(subscription.nextBillAt, {
        absolute: { accuracy: 'day' },
      }),
    });
  } else if (subscription?.end) {
    text = t['com.hive.payment.billing-setting.due-date.description']({
      dueDate: i18nTime(subscription.end, {
        absolute: { accuracy: 'day' },
      }),
    });
  }

  return text ? (
    <>
      <br />
      {text}
    </>
  ) : null;
};

const PlanAction = ({
  plan,
  subscriptionStatus,
  gotoPlansSetting,
  isCanceled,
}: {
  plan: string | null;
  gotoPlansSetting: () => void;
  subscriptionStatus?: SubscriptionStatus;
  isCanceled?: boolean;
}) => {
  const t = useI18n();
  const [cancelOpen, setCancelOpen] = useState(false);

  const handleCancelClick = useCallback(() => {
    setCancelOpen(true);
  }, []);

  // Show cancel button if user has an active subscription and hasn't already canceled
  const showCancelButton =
    plan === SubscriptionPlan.Team &&
    !isCanceled &&
    (subscriptionStatus === SubscriptionStatus.Active ||
      subscriptionStatus === SubscriptionStatus.Trialing);

  return (
    <>
      <div className={styles.planActions}>
        <Button
          className={styles.planAction}
          variant="primary"
          onClick={gotoPlansSetting}
        >
          {plan === SubscriptionPlan.Team
            ? t['com.hive.payment.billing-setting.change-plan']()
            : t['com.hive.payment.billing-setting.upgrade']()}
        </Button>
        {showCancelButton && (
          <CancelAction open={cancelOpen} onOpenChange={setCancelOpen}>
            <Button
              className={styles.planAction}
              variant="secondary"
              onClick={handleCancelClick}
            >
              {t['com.hive.payment.billing-setting.cancel-subscription']?.() ??
                'Cancel Subscription'}
            </Button>
          </CancelAction>
        )}
      </div>
      {subscriptionStatus === SubscriptionStatus.PastDue ? (
        <PaymentMethodUpdater
          inCardView
          className={styles.manageMentInCard}
          variant="primary"
        />
      ) : null}
    </>
  );
};
